/**
 * The ONE palette a person, a company or a service notification can be
 * coloured with.
 *
 * Four copies of this list used to live in four files, and one of them —
 * Company Settings — had drifted to a different set of names ("Lavender",
 * "Peach", "Pewter") over different hex values, so the same stored key
 * `peach` was a clownfish-orange dot on one screen and a yellow one on the
 * next (Andrew, 2026-09-29: "There should only be one set of colours").
 *
 * `name` is the stored key (`users.theme_color`, `companies.theme_color`,
 * `service_notifications.background_color`) and must not change. `label` is
 * what the picker shows. `hex` is the swatch on a light surface, `dark` on a
 * dark one — pass `effectiveTheme` to `brandColourSwatch` and it picks.
 */
import { SALEFISH_BRAND_DARK_HEX, SALEFISH_BRAND_HEX } from "@shared/registrationStyling";

export interface BrandColour {
  name: string;
  label: string;
  /** Translation key for `label` — resolve with `t(c.labelKey, c.label)`. Absent for the brand name. */
  labelKey?: string;
  hex: string;
  dark: string;
}

export const BRAND_COLOURS: readonly BrandColour[] = [
  { name: "salefish", label: "SaleFish", hex: SALEFISH_BRAND_HEX, dark: SALEFISH_BRAND_DARK_HEX },
  { name: "lavender", label: "Seahorse", labelKey: "orgColourSeahorse", hex: "#E6E6FA", dark: "#4B4B7A" },
  { name: "periwinkle", label: "Periwinkle", labelKey: "orgColourPeriwinkle", hex: "#CCCCFF", dark: "#5555AA" },
  { name: "sky", label: "Ocean", labelKey: "orgColourOcean", hex: "#87CEEB", dark: "#3A6B84" },
  { name: "pewter", label: "Seal", labelKey: "orgColourSeal", hex: "#B0C4DE", dark: "#5A6D82" },
  { name: "mint", label: "Seaweed", labelKey: "orgColourSeaweed", hex: "#98D8C8", dark: "#4A7B6C" },
  { name: "sage", label: "Kelp", labelKey: "orgColourKelp", hex: "#9DC183", dark: "#566D44" },
  { name: "peach", label: "Clownfish", labelKey: "orgColourClownfish", hex: "#FFDAB9", dark: "#A36B3A" },
  { name: "gold", label: "Starfish", labelKey: "orgColourStarfish", hex: "#FFD700", dark: "#8B7500" },
  { name: "coral", label: "Coral", labelKey: "orgColourCoral", hex: "#F88379", dark: "#A04540" },
  { name: "rose", label: "Salmon", labelKey: "orgColourSalmon", hex: "#FFB6C1", dark: "#A05B6A" },
];

export type BrandColourName = (typeof BRAND_COLOURS)[number]["name"];

/** The entry for a stored key, or the SaleFish default for anything unknown. */
export function brandColour(name: string | null | undefined): BrandColour {
  return BRAND_COLOURS.find((c) => c.name === name) ?? BRAND_COLOURS[0];
}

/** The swatch colour for the surface the reader is on. */
export function brandColourSwatch(entry: BrandColour, effectiveTheme: string | undefined): string {
  return effectiveTheme === "dark" ? entry.dark : entry.hex;
}
